/**
 * publishTheme — write theme content + terminology and flip the brand live (the
 * existing "Save & publish" path). Uses `publishBrandTheme`. Emits ONE event:
 * "Theme Published".
 *
 * `channel` reflects what is PERSISTED: `"recipe+6hex"` when a recipe rides in
 * `treatment` (#4827), else `"legacy-6hex"` (the 6 hexes only). The 6 hexes are
 * authoritative either way; the recipe is additive. A v2-aware embed
 * regenerating the theme live is a separate axis, tracked by "Preview Driven".
 * Returns the stored brand and which refused pins the write cleared.
 */
import type { BrandData } from "@/data/brands-api";
import type { PinVerdict } from "@/data/pin-verdicts";
import { track } from "@/lib/analytics";
import { composeThemeWrite, recipeProps } from "./_content";
import type { PublishThemeInput, ThemeCommandClient } from "./types";

export async function publishTheme(
  input: PublishThemeInput,
  deps: { client: Pick<ThemeCommandClient, "publishBrandTheme"> },
): Promise<{ brand: BrandData; cleared: PinVerdict["pin"][] }> {
  const { content, cleared } = composeThemeWrite(input);
  const brand = await deps.client.publishBrandTheme({
    brandId: input.brandId,
    themeId: input.themeId,
    content,
    terminology: input.terminology,
  });

  track("Theme Published", {
    brandId: input.brandId,
    themeId: input.themeId,
    channel: input.recipe ? "recipe+6hex" : "legacy-6hex",
    ...recipeProps(input.recipe),
  });

  return { brand, cleared };
}
